import React, { useState, useEffect } from 'react';
import type { Theme } from '@material-ui/core';
import {
    Step,
    Stepper,
    StepButton,
    Box,
    useMediaQuery,
    StepLabel,
} from '@material-ui/core';
import { Block } from '@klp/design';
import type { Aktivitet } from '@klp/formgenerator/interfaces';
import classNames from 'classnames';
import { ConditionalStepIcon } from './ConditionalStepIcon';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { useEqualHeightByClass } from '@forvalter/utils/visual/useEqualHeightByClass';
import { LEVERANDOR_REGISTRERING_STATUS } from '../types';
import {
    formatDateWithTime,
    parseSafeDate,
} from '@klp/utils/dateTimeFormatter';

const styles = (theme: Theme) => ({
    stepperContainer: {
        width: '100%',
    },
    stepper: {
        padding: theme.spacing(1),
    },
    stepButton: {
        marginLeft: theme.spacing(-1.5),
        marginRight: theme.spacing(-1.5),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        overflowWrap: 'anywhere',
    },
    stepLabel: {
        width: '100%',
        '& .MuiStepLabel-label': {
            border: `1px solid ${theme.palette.primary.light}`,
            borderRadius: theme.shape.borderRadius,
            '&.MuiStepLabel-active': {
                borderColor: theme.palette.secondary.dark,
                '& .SecondRow': {
                    backgroundColor: theme.palette.secondary.veryLight,
                },
            },
            '& > *': {
                paddingLeft: theme.spacing(1),
                paddingRight: theme.spacing(1),
            },
        },
    },
    rejectedStepperButton: {
        '& .MuiStepIcon-completed': {
            color: 'red',
        },
        '& .MuiStepIcon-active': {
            color: 'red',
        },
    },
    firstRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: `clamp(0.85em, calc(0.85em + 0.1vw), 1.2em)`,
        lineHeight: 1.1,
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
    },
    secondRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(0.5),
        fontStyle: 'italic',
        fontSize: `clamp(0.85em, calc(0.85em + 0.1vw), 1.2em)`,
        lineHeight: 1.1,
        backgroundColor: theme.palette.primary?.veryLight,
    },
    thirdRow: {
        color: theme.palette.primary.dark,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: `clamp(0.85em, calc(0.85em + 0.1vw), 1.2em)`,
        lineHeight: 1.1,
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
    },
});

type LeverandorStepperProps = {
    aktiviteter: Array<Aktivitet>;
    processStatus?: string;
    callback: (activeStep: number) => void;
};

export const LeverandorStepper: React.FC<LeverandorStepperProps> = ({
    aktiviteter,
    processStatus,
    callback,
}) => {
    const classes = useKlpStyles({}, styles);
    const [activeStep, setActiveStep] = useState(0);
    const isMediumScreen = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down('sm'),
    );

    const lastCompletedStep = aktiviteter.findLastIndex(
        (a) => a.id !== null && a.utfortDato !== null,
    );

    const aktivitetSignature = aktiviteter
        .map((a) => `${a.id}-${a.utfortDato}`)
        .join(',');

    useEffect(() => {
        const nextActive = aktiviteter.findIndex(
            (a) => a.id !== null && a.utfortDato === null,
        );

        const newStep =
            nextActive !== -1 ? nextActive : Math.max(lastCompletedStep, 0);
        setActiveStep((prev) => (prev !== newStep ? newStep : prev));
        callback(newStep);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aktivitetSignature]);

    useEqualHeightByClass(classes.firstRow, isMediumScreen);
    useEqualHeightByClass(classes.secondRow, isMediumScreen);
    useEqualHeightByClass(classes.thirdRow, isMediumScreen);
    useEqualHeightByClass(classes.stepLabel, isMediumScreen);

    return (
        <Box
            className={classes.stepperContainer}
            flexDirection={isMediumScreen ? 'column' : 'row'}
        >
            <Stepper
                nonLinear={false}
                activeStep={activeStep}
                orientation={isMediumScreen ? 'vertical' : 'horizontal'}
                alternativeLabel={!isMediumScreen}
                className={classes.stepper}
            >
                {aktiviteter.map((aktivitet, index) => {
                    const isCompleted = Boolean(
                        aktivitet.id &&
                        aktivitet.utfortDato &&
                        activeStep !== index,
                    );
                    const isRejected =
                        processStatus ===
                            LEVERANDOR_REGISTRERING_STATUS.AVVIST &&
                        lastCompletedStep === index;

                    return (
                        <Step
                            key={`${aktivitet.id}-${index}`}
                            completed={isCompleted}
                            active={activeStep === index}
                            style={{
                                cursor: aktivitet.id
                                    ? 'pointer'
                                    : 'not-allowed',
                            }}
                            data-testid={`LeverandorStepper__Step--${index}`}
                        >
                            <StepButton
                                onClick={() => {
                                    if (aktivitet.id) {
                                        setActiveStep(index);
                                        callback(index);
                                    }
                                }}
                                disabled={!aktivitet.id}
                                className={classNames(classes.stepButton, {
                                    [classes.rejectedStepperButton]: isRejected,
                                })}
                                data-testid={`step-button-${isRejected ? 'rejected' : index}`}
                            >
                                <StepLabel
                                    StepIconComponent={(iconProps) => (
                                        <ConditionalStepIcon
                                            {...iconProps}
                                            translationKey="leverandor.skipActivity"
                                            skalHoppeOver={
                                                aktivitet.skalHoppeOver
                                            }
                                        />
                                    )}
                                    className={classes.stepLabel}
                                >
                                    <Block
                                        translation={
                                            aktivitet.navn ??
                                            'missing activity name'
                                        }
                                        className={classNames(
                                            'AktivitetNavn',
                                            classes.firstRow,
                                        )}
                                    />
                                    <Block
                                        translation={`leverandor.table.roller.${aktivitet.kandidatGruppeEier}`}
                                        className={classNames(
                                            'SecondRow',
                                            classes.secondRow,
                                        )}
                                    />
                                    {aktivitet.utfortDato ? (
                                        <Block
                                            className={classNames(
                                                classes.thirdRow,
                                            )}
                                            translation="leverandor.completedBy"
                                            translationOptions={{
                                                name: aktivitet.tildeltTil,
                                                date: formatDateWithTime(
                                                    parseSafeDate(
                                                        aktivitet.utfortDato,
                                                    ),
                                                ),
                                            }}
                                        />
                                    ) : aktivitet.startDato ? (
                                        <Block
                                            className={classNames(
                                                classes.thirdRow,
                                            )}
                                            translation="leverandor.ansvarlig"
                                            translationOptions={{
                                                name: aktivitet.tildeltTil,
                                            }}
                                        />
                                    ) : null}
                                </StepLabel>
                            </StepButton>
                        </Step>
                    );
                })}
            </Stepper>
        </Box>
    );
};
